import {
  queryOptions,
  useQuery,
  useMutation,
  useQueryClient,
  infiniteQueryOptions,
  useInfiniteQuery,
  keepPreviousData,
} from "@tanstack/react-query";

import type { EngagementStatus } from "~/lib/engagement-status";
import {
  createEngagementFn,
  getActiveEngagementsFn,
  getAllEngagementByCoachFn,
  getCoachClientProgramSummaryFn,
  getUserInfoForCoachEngagementFn,
  getCoachInfoForEngagementFn,
  getCoachClientRelationshipFn,
  getEngagementSessionProgressFn,
  getAllResourceByUploadModelFn,
  getEngagementAgreementsByCoachFn,
  getPaymentInfoFn,
  getPaymentHistoryFn,
  getCoachEngagementCardsFn,
  deleteInvoiceFn,
  cancelInvoiceFn,
  editEngagementInvoiceFn,
  updateEngagementStatusFn,
  getUserEngagementsFn,
  getEngagementAgreementByClientFn,
  getUserInfoForClientEngagementFn,
} from "~/lib/functions/engagement";
import { createInvoiceForCoachFn } from "~/lib/functions/invoice";
import type { CoachEngagementCardsQuery } from "~/lib/schemas/engagement";
import { createAndUploadFile } from "~/lib/upload-file";

type CoachEngagementCardsSort = CoachEngagementCardsQuery["sort"];

export const engagementQueryKeys = {
  all: ["engagement"] as const,
  active: () => [...engagementQueryKeys.all, "active"] as const,
  cards: (
    page?: number,
    limit?: number,
    statuses?: EngagementStatus[],
    sort?: CoachEngagementCardsSort,
  ) =>
    [
      ...engagementQueryKeys.all,
      "cards",
      { page, limit, statuses, sort },
    ] as const,
  clientProgramSummary: () =>
    [...engagementQueryKeys.all, "client-program-summary"] as const,
  cardsInfinite: (limit?: number) =>
    [...engagementQueryKeys.all, "cards-infinite", { limit }] as const,
  coachCardsInfinite: (limit?: number) =>
    [...engagementQueryKeys.all, "coach-cards-infinite", { limit }] as const,
  userEngagements: (page?: number, limit?: number, status?: EngagementStatus) =>
    [
      ...engagementQueryKeys.all,
      "user-engagements",
      { page, limit, status },
    ] as const,
  userEngagementsInfinite: (limit?: number, status?: EngagementStatus) =>
    [
      ...engagementQueryKeys.all,
      "user-engagements-infinite",
      { limit, status },
    ] as const,
  clientRelationships: (search?: string) =>
    [...engagementQueryKeys.all, "client-relationships", { search }] as const,
  userInfo: (engagementId: string) =>
    [...engagementQueryKeys.all, "user-info", engagementId] as const,
  coachInfo: (engagementId: string) =>
    [...engagementQueryKeys.all, "coach-info", engagementId] as const,
  sessionProgress: (engagementId: string) =>
    [...engagementQueryKeys.all, "session-progress", engagementId] as const,
  resourcesByUploadModel: (engagementId: string, uploadModel: string) =>
    [
      ...engagementQueryKeys.all,
      "resources-by-upload-model",
      engagementId,
      uploadModel,
    ] as const,
  agreementsByCoach: (engagementId: string, page?: number, limit?: number) =>
    [
      ...engagementQueryKeys.all,
      "agreements-by-coach",
      engagementId,
      { page, limit },
    ] as const,
  agreementsByCoachInfinite: (engagementId: string, limit?: number) =>
    [
      ...engagementQueryKeys.all,
      "agreements-by-coach-infinite",
      engagementId,
      { limit },
    ] as const,
  agreementsByClientInfinite: (engagementId: string, limit?: number) =>
    [
      ...engagementQueryKeys.all,
      "agreements-by-client-infinite",
      engagementId,
      { limit },
    ] as const,
  paymentInfo: (engagementId: string) =>
    [...engagementQueryKeys.all, "payment-info", engagementId] as const,
  paymentHistory: (
    engagementId: string,
    filter?: string,
    page?: number,
    limit?: number,
  ) =>
    [
      ...engagementQueryKeys.all,
      "payment-history",
      engagementId,
      { filter, page, limit },
    ] as const,
  paymentHistoryAll: (engagementId: string) =>
    [...engagementQueryKeys.all, "payment-history", engagementId] as const,
};

export const activeEngagementsQueryOptions = (coachId: string) =>
  queryOptions({
    queryKey: engagementQueryKeys.active(),
    queryFn: () => getActiveEngagementsFn({ data: { coachId } }),
    enabled: !!coachId,
  });

export const useActiveEngagements = (coachId?: string) => {
  return useQuery({
    ...activeEngagementsQueryOptions(coachId || ""),
    enabled: !!coachId,
  });
};

export const allEngagementsByCoachQueryOptions = (
  page: number,
  limit: number,
  statuses?: EngagementStatus[],
  sort?: CoachEngagementCardsSort,
) =>
  queryOptions({
    queryKey: engagementQueryKeys.cards(page, limit, statuses, sort),
    queryFn: () =>
      getAllEngagementByCoachFn({ data: { page, limit, statuses, sort } }),
    placeholderData: keepPreviousData, // Prevent 0 rows flash during pagination
  });

export const useAllEngagementsByCoach = (
  page: number,
  limit: number,
  statuses?: EngagementStatus[],
  sort?: CoachEngagementCardsSort,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    ...allEngagementsByCoachQueryOptions(page, limit, statuses, sort),
    ...options,
  });
};

// Lives under `engagementQueryKeys.all`, so every program mutation that
// refreshes the cards refreshes these counts too.
export const clientProgramSummaryQueryOptions = () =>
  queryOptions({
    queryKey: engagementQueryKeys.clientProgramSummary(),
    queryFn: () => getCoachClientProgramSummaryFn(),
  });

export const useClientProgramSummary = () => {
  return useQuery(clientProgramSummaryQueryOptions());
};

export const coachEngagementCardsInfiniteQueryOptions = (limit: number) =>
  infiniteQueryOptions({
    queryKey: engagementQueryKeys.coachCardsInfinite(limit),
    queryFn: ({ pageParam = 1 }) =>
      getCoachEngagementCardsFn({ data: { page: pageParam, limit } }),
    getNextPageParam: (lastPage, _allPages) => {
      if (
        lastPage &&
        "pagination" in lastPage &&
        lastPage.pagination?.hasNextPage
      ) {
        return lastPage.pagination.nextPage;
      }
      return undefined;
    },
    initialPageParam: 1,
  });

export const useInfiniteCoachEngagementCards = (limit: number) => {
  return useInfiniteQuery(coachEngagementCardsInfiniteQueryOptions(limit));
};

export const engagementCardsInfiniteQueryOptions = (limit: number) =>
  infiniteQueryOptions({
    queryKey: engagementQueryKeys.cardsInfinite(limit),
    queryFn: ({ pageParam = 1 }) =>
      getAllEngagementByCoachFn({ data: { page: pageParam, limit } }),
    getNextPageParam: (lastPage, _allPages) => {
      if (
        lastPage &&
        "pagination" in lastPage &&
        lastPage.pagination?.hasNextPage
      ) {
        return lastPage.pagination.nextPage;
      }
      return undefined;
    },
    initialPageParam: 1,
  });

export const useInfiniteEngagementCards = (limit: number) => {
  return useInfiniteQuery(engagementCardsInfiniteQueryOptions(limit));
};

export const coachClientRelationshipsQueryOptions = (search?: string) =>
  queryOptions({
    queryKey: engagementQueryKeys.clientRelationships(search),
    queryFn: () => getCoachClientRelationshipFn({ data: { search } }),
    placeholderData: keepPreviousData,
    enabled: true,
  });

export const useCoachClientRelationships = (search?: string) => {
  return useQuery(coachClientRelationshipsQueryOptions(search));
};

export const useCreateEngagement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createEngagementFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: engagementQueryKeys.all });
    },
  });
};

export const userInfoForEngagementQueryOptions = (engagementId: string) =>
  queryOptions({
    queryKey: [...engagementQueryKeys.userInfo(engagementId), "coach"],
    queryFn: () => getUserInfoForCoachEngagementFn({ data: { engagementId } }),
    enabled: !!engagementId,
  });

export const userInfoForClientEngagementQueryOptions = (engagementId: string) =>
  queryOptions({
    queryKey: [...engagementQueryKeys.userInfo(engagementId), "client"],
    queryFn: () => getUserInfoForClientEngagementFn({ data: { engagementId } }),
    enabled: !!engagementId,
  });

export const useUserInfoForEngagement = (engagementId: string) => {
  return useQuery(userInfoForEngagementQueryOptions(engagementId));
};

export const useUserInfoForClientEngagement = (engagementId: string) => {
  return useQuery(userInfoForClientEngagementQueryOptions(engagementId));
};

export const coachInfoForEngagementQueryOptions = (engagementId: string) =>
  queryOptions({
    queryKey: engagementQueryKeys.coachInfo(engagementId),
    queryFn: () => getCoachInfoForEngagementFn({ data: { engagementId } }),
    enabled: !!engagementId,
  });

export const useCoachInfoForEngagement = (engagementId: string) => {
  return useQuery(coachInfoForEngagementQueryOptions(engagementId));
};

export const engagementSessionProgressQueryOptions = (engagementId: string) =>
  queryOptions({
    queryKey: engagementQueryKeys.sessionProgress(engagementId),
    queryFn: () => getEngagementSessionProgressFn({ data: { engagementId } }),
    enabled: !!engagementId,
  });

export const useEngagementSessionProgress = (engagementId: string) => {
  return useQuery(engagementSessionProgressQueryOptions(engagementId));
};

export const resourcesByUploadModelQueryOptions = (
  engagementId: string,
  uploadModel: "file" | "agreement",
) =>
  queryOptions({
    queryKey: engagementQueryKeys.resourcesByUploadModel(
      engagementId,
      uploadModel,
    ),
    queryFn: () =>
      getAllResourceByUploadModelFn({ data: { engagementId, uploadModel } }),
    enabled: !!engagementId,
  });

export const useResourcesByUploadModel = (
  engagementId: string,
  uploadModel: "file" | "agreement",
) => {
  return useQuery(
    resourcesByUploadModelQueryOptions(engagementId, uploadModel),
  );
};

export const useCreateAgreementResource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      engagementId: string;
      title: string;
      file: File;
      expirationDate?: Date;
    }) => {
      return createAndUploadFile(
        {
          engagementId: data.engagementId,
          title: data.title,
          filename: data.file.name,
          contentType: data.file.type,
          uploadModel: "agreement",
          expirationDate: data.expirationDate,
        },
        data.file,
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: engagementQueryKeys.resourcesByUploadModel(
          variables.engagementId,
          "agreement",
        ),
      });
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          return (
            Array.isArray(queryKey) &&
            queryKey.includes("engagement") &&
            queryKey.includes("agreements-by-coach-infinite") &&
            queryKey.includes(variables.engagementId)
          );
        },
      });
    },
  });
};

export const agreementsByCoachInfiniteQueryOptions = (
  engagementId: string,
  limit: number,
) =>
  infiniteQueryOptions({
    queryKey: engagementQueryKeys.agreementsByCoachInfinite(
      engagementId,
      limit,
    ),
    queryFn: ({ pageParam = 1 }) =>
      getEngagementAgreementsByCoachFn({
        data: { engagementId, page: pageParam, limit },
      }),
    getNextPageParam: (lastPage, _allPages) => {
      if (
        lastPage &&
        "pagination" in lastPage &&
        lastPage.pagination?.hasNextPage
      ) {
        return lastPage.pagination.nextPage;
      }
      return undefined;
    },
    initialPageParam: 1,
  });

export const agreementsByClientInfiniteQueryOptions = (
  engagementId: string,
  limit: number,
) =>
  infiniteQueryOptions({
    queryKey: engagementQueryKeys.agreementsByClientInfinite(
      engagementId,
      limit,
    ),
    queryFn: ({ pageParam = 1 }) =>
      getEngagementAgreementByClientFn({
        data: { engagementId, page: pageParam, limit },
      }),
    getNextPageParam: (lastPage, _allPages) => {
      if (
        lastPage &&
        "pagination" in lastPage &&
        lastPage.pagination?.hasNextPage
      ) {
        return lastPage.pagination.nextPage;
      }
      return undefined;
    },
    initialPageParam: 1,
  });

export const useInfiniteAgreementsByCoach = (
  engagementId: string,
  limit: number,
) => {
  return useInfiniteQuery(
    agreementsByCoachInfiniteQueryOptions(engagementId, limit),
  );
};

export const useInfiniteAgreementsByClient = (
  engagementId: string,
  limit: number,
) => {
  return useInfiniteQuery(
    agreementsByClientInfiniteQueryOptions(engagementId, limit),
  );
};

export const paymentInfoQueryOptions = (engagementId: string) =>
  queryOptions({
    queryKey: engagementQueryKeys.paymentInfo(engagementId),
    queryFn: () => getPaymentInfoFn({ data: { engagementId } }),
    enabled: !!engagementId,
  });

export const usePaymentInfo = (engagementId: string) => {
  return useQuery(paymentInfoQueryOptions(engagementId));
};

export const paymentHistoryQueryOptions = (
  engagementId: string,
  filter?: "paid" | "pending" | "future" | "cancelled",
  page: number = 1,
  limit: number = 10,
) =>
  queryOptions({
    queryKey: engagementQueryKeys.paymentHistory(
      engagementId,
      filter,
      page,
      limit,
    ),
    queryFn: () =>
      getPaymentHistoryFn({
        data: { engagementId, filter, page, limit },
      }),
    enabled: !!engagementId,
  });

export const usePaymentHistory = (
  engagementId: string,
  filter?: "paid" | "pending" | "future" | "cancelled",
  page: number = 1,
  limit: number = 10,
) => {
  return useQuery(
    paymentHistoryQueryOptions(engagementId, filter, page, limit),
  );
};

export const useDeleteInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteInvoiceFn,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: engagementQueryKeys.paymentHistoryAll(
          variables.data.engagementId,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: engagementQueryKeys.paymentInfo(variables.data.engagementId),
      });
    },
  });
};

export const useCancelInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cancelInvoiceFn,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: engagementQueryKeys.paymentHistoryAll(
          variables.data.engagementId,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: engagementQueryKeys.paymentInfo(variables.data.engagementId),
      });
    },
  });
};

export const useEditInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: editEngagementInvoiceFn,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: engagementQueryKeys.paymentHistoryAll(
          variables.data.engagementId,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: engagementQueryKeys.paymentInfo(variables.data.engagementId),
      });
    },
  });
};

export const useCreateInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createInvoiceForCoachFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: engagementQueryKeys.all,
      });
    },
  });
};

export const useUpdateEngagementStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateEngagementStatusFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: engagementQueryKeys.all,
      });
    },
  });
};

export const userEngagementsInfiniteQueryOptions = (
  limit: number,
  status?: EngagementStatus,
) =>
  infiniteQueryOptions({
    queryKey: engagementQueryKeys.userEngagementsInfinite(limit, status),
    queryFn: ({ pageParam = 1 }) =>
      getUserEngagementsFn({ data: { page: pageParam, limit, status } }),
    getNextPageParam: (lastPage, _allPages) => {
      if (
        lastPage &&
        "pagination" in lastPage &&
        lastPage.pagination?.hasNextPage
      ) {
        return lastPage.pagination.nextPage;
      }
      return undefined;
    },
    initialPageParam: 1,
  });

export const useInfiniteUserEngagements = (
  limit: number,
  status?: EngagementStatus,
) => {
  return useInfiniteQuery(userEngagementsInfiniteQueryOptions(limit, status));
};

export const userEngagementsQueryOptions = (
  page: number,
  limit: number,
  status?: EngagementStatus,
) =>
  queryOptions({
    queryKey: engagementQueryKeys.userEngagements(page, limit, status),
    queryFn: () => getUserEngagementsFn({ data: { page, limit, status } }),
  });

export const useUserEngagements = (
  page: number,
  limit: number,
  status?: EngagementStatus,
) => {
  return useQuery(userEngagementsQueryOptions(page, limit, status));
};
